/*
 * @Author: Json Chen
 * @Date: 2021-06-28 10:00:19
 * @LastEditTime: 2022-06-02 14:28:33
 * @LastEditors: Json Chen
 * @Description: PriceTier 的列表展示,外层以 Form.Item 包裹,整个数组作为 value
 * @FilePath: /unifyxp-v2-store-portal/src/pages/Settings/Product/Components/PriceTierTable.tsx
 */
import { Button, SizeFittingTable } from '@/src/components/Antd';
import ModalDelHOC from '@/src/constrains/ModalDelHOC';
import React from 'react';
import styles from '../style.module.less';
import PriceTierModalForm from './PriceTierModalForm';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet } from 'src/tools/tools';
import { useRequest } from 'ahooks';
import { getCheckPriceTier } from 'src/request/settings/product';
import { get } from 'lodash';
import { Spin } from 'antd';
import { modal } from 'src/utils/Modal';

interface PriceTier {
  uuid?: string;
  title: string;
  currencyCode: string;
  remarks: string;
  deletable: boolean;
}

interface GroupedPriceTier {
  title: string;
  tiers: PriceTier[];
  remarks: string;
  currencyCode: string;
}

const TIPCONTENTSTRATEGY = ({ getI18n }) => ({
  confirm: {
    title: getI18n('storeProductSetting.detail.deletePriceTier'),
    content: getI18n('storeProductSetting.detail.deletePriceTierContent'),
    buttonType: 'primary',
    okText: getI18n('general.confirm'),
  },
  delete: {
    title: getI18n('storeProductSetting.detail.deletePriceTier'),
    content: getI18n('storeProductSetting.detail.deletePriceTierContent2'),
    buttonType: 'danger',
    okText: getI18n('general.delete'),
  },
});

// 根据title分组
const groupPriceTiersByTitle = (
  priceTierList: PriceTier[] = [],
): GroupedPriceTier[] => {
  const groupedMap = priceTierList.reduce((acc, tier) => {
    if (!acc[tier.title]) {
      acc[tier.title] = {
        title: tier.title,
        tiers: [],
        remarks: '',
        currencyCode: '',
      };
    }
    acc[tier.title].tiers.push(tier);
    return acc;
  }, {} as Record<string, GroupedPriceTier>);

  Object.keys(groupedMap).forEach((title) => {
    const group = get(groupedMap, title);
    group.remarks = get(group, 'tiers[0].remarks', '');
    group.currencyCode = group.tiers.map((t) => t.currencyCode).join(', ');
  });

  return Object.values(groupedMap);
};

// 用于获取一个分组中是否所有项都可删除
const isGroupDeletable = (tiers: PriceTier[]) => {
  return tiers.every((tier) => tier.deletable);
};

const PriceTierTable = ({
  value: priceTierList = [],
  onChange,
  priceTierDefaultCurrency,
  priceTierCurrencyOptions,
  onDelClick,
  disabled,
}) => {
  const i18n = useI18n();
  const getI18n = (str) => {
    return i18nGet(i18n, str);
  };
  const [priceTierModalVisible, setAddPriceTierModalVisible] =
    React.useState<boolean>(false);
  const [priceTierIndex, setPriceTierIndex] = React.useState<number>(-1);
  const [editingTitle, setEditingTitle] = React.useState<string>('');

  // 将原始数据转换为分组显示
  const groupedPriceTiers = React.useMemo(() => {
    return groupPriceTiersByTitle(priceTierList);
  }, [priceTierList]);

  const showAddPriceTierModal = () => {
    // 检查分组数量是否已达到上限10个
    if (groupedPriceTiers.length >= 10) {
      return;
    }
    setAddPriceTierModalVisible(true);
  };

  const onPriceTierActionEditClick = (
    group: GroupedPriceTier,
    index: number,
  ) => {
    setPriceTierIndex(index);
    setEditingTitle(group.title);
    setAddPriceTierModalVisible(true);
  };

  const hideAddPriceTierModal = () => {
    setAddPriceTierModalVisible(false);
    setPriceTierIndex(-1);
    setEditingTitle('');
  };

  const onConfirm = (values) => {
    if (priceTierIndex === -1) {
      const { title, remarks, applicableCurrency } = values;

      const newTiers = applicableCurrency.map((currencyCode) => ({
        title,
        currencyCode,
        remarks,
        deletable: true,
      }));

      onChange(priceTierList.concat(newTiers));
    } else {
      const { title, remarks, applicableCurrency: toKeepCurrencies } = values;
      const oldTitle = editingTitle;

      const oldCurrencies = priceTierList
        .filter((item) => item.title === oldTitle)
        .map((item) => item.currencyCode);

      const toAddCurrencies = toKeepCurrencies.filter(
        (c) => !oldCurrencies.includes(c),
      );

      const toRemoveCurrencies = oldCurrencies.filter(
        (c) => !toKeepCurrencies.includes(c),
      );

      const updatedList = priceTierList
        .filter(
          (item) =>
            !(
              item.title === oldTitle &&
              toRemoveCurrencies.includes(item.currencyCode)
            ),
        )
        .map((item) => {
          if (item.title === oldTitle) {
            return { ...item, title, remarks };
          }
          return item;
        });

      const newItems = toAddCurrencies.map((currencyCode) => ({
        title,
        currencyCode,
        remarks,
        deletable: true,
      }));

      onChange([...updatedList, ...newItems]);
    }

    hideAddPriceTierModal();
  };

  // 分组删除
  const handleOnDelete = (index: number) => {
    const group = groupedPriceTiers[index];
    if (group) {
      const updatedList = priceTierList.filter(
        (item) => item.title !== group.title,
      );
      onChange(updatedList);
    }
  };

  const { run: handleOnCheckTier, loading: checkLoading } = useRequest(
    (uuid: string, index: number) => {
      return getCheckPriceTier(uuid);
    },
    {
      manual: true,
      onSuccess: (res, param) => {
        const index = get(param, '1');
        if (res) {
          handleOnDelete(index);
        } else {
          modal.error({
            content: getI18n(
              'storeProductSetting.detail.deletePriceTierContent',
            ),
            onOkText: getI18n('general.confirm'),
            onOk: () => {
              return;
            },
          });
        }
      },
    },
  );

  const priceTierColumns = [
    {
      title: getI18n('storeProductSetting.detail.priceTier'),
      key: 'index',
      dataIndex: 'index',
      default: true,
      width: 90,
      childGenerate: (_, record, index) => {
        return <div className={styles.tb}>{index + 1}.</div>;
      },
    },
    {
      title: getI18n('storeProductSetting.detail.priceTierTitle'),
      dataIndex: 'title',
      key: 'title',
      default: true,
      width: 352,
      textWrap: 'word-break',
      childGenerate: (text: string) => {
        return <div className={styles.tb}>{text}</div>;
      },
    },
    {
      title: getI18n('storeProductSetting.detail.priceTierCurrency'),
      key: 'currencyCode',
      dataIndex: 'currencyCode',
      default: true,
      width: 192,
      textWrap: 'word-break',
      childGenerate: (text: string) => {
        return <div className={styles.tb}>{text}</div>;
      },
    },
    {
      title: getI18n('storeProductSetting.detail.priceTierRemarks'),
      key: 'remarks',
      dataIndex: 'remarks',
      default: true,
      width: 320,
      textWrap: 'word-break',
      childGenerate: (text: string) => {
        return <div className={styles.tb}>{text}</div>;
      },
    },
    disabled
      ? {}
      : {
          title: getI18n(
            'general.advancedFilters.commonStoreProductsWithTable.actions',
          ),
          default: true,
          optional: true,
          align: 'center',
          width: 128,
          childGenerate: (_, record: GroupedPriceTier, index: number) => {
            // 检查是否可以删除整个分组
            const deletable = record.tiers && isGroupDeletable(record.tiers);
            const anyUuid =
              record.tiers && record.tiers.length > 0
                ? record.tiers[0].uuid
                : null;

            return (
              <div className={styles.action}>
                <span
                  className={styles.primary}
                  onClick={() => onPriceTierActionEditClick(record, index)}>
                  {getI18n('general.edit')}
                </span>
                {deletable && (
                  <span
                    className={styles.danger}
                    onClick={() => {
                      if (anyUuid) {
                        handleOnCheckTier(anyUuid, index);
                      } else {
                        handleOnDelete(index);
                      }
                    }}>
                    {getI18n('general.delete')}
                  </span>
                )}
              </div>
            );
          },
        },
  ];

  return (
    <>
      {groupedPriceTiers.length ? (
        <Spin spinning={checkLoading}>
          <SizeFittingTable
            rowKey='title'
            pagination={false}
            id='StoreSettingsProductPriceTier'
            className={styles.table}
            dataSource={groupedPriceTiers}
            columns={priceTierColumns}
          />
        </Spin>
      ) : (
        <div className={styles.noresult}>
          {getI18n('storeProductSetting.detail.noproductpricetier')}
        </div>
      )}
      <Button
        type='primary'
        onClick={showAddPriceTierModal}
        disabled={disabled || groupedPriceTiers.length >= 10}>
        {getI18n('storeProductSetting.detail.addPriceTier')}
      </Button>
      <PriceTierModalForm
        visible={priceTierModalVisible}
        onConfirm={onConfirm}
        onCancel={hideAddPriceTierModal}
        priceTierDefaultCurrency={priceTierDefaultCurrency}
        priceTierCurrencyOptions={priceTierCurrencyOptions}
        priceTierList={priceTierList}
        priceTierIndex={priceTierIndex}
        editingTitle={editingTitle}
        groupItemLength={groupedPriceTiers?.length}
      />
    </>
  );
};

export default ModalDelHOC(PriceTierTable, {
  modalDataStrategy: TIPCONTENTSTRATEGY,
  canDel: ({ list, index }) => {
    // 检查分组是否可删除
    const group = groupPriceTiersByTitle(list)[index];
    return group && group.tiers && isGroupDeletable(group.tiers);
  },
});
